import type { ReactNode } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CircleDot,
  Clock,
  Download,
  Gauge,
  Inbox,
  Layers,
  ShieldCheck,
  Sparkles,
  Timer,
} from 'lucide-react';
import { canAccess, capabilitiesIn } from '../lib/access.js';
import {
  api,
  type AgentWorkloadReport,
  type AutomationReport,
  type SuggestedRepliesReport,
  type BacklogAgeingReport,
  type BlockedTimeReport,
  type DemandShapeReport,
  type FieldHealthRow,
  type Overview,
  type RecurrenceReport,
  type ResponseQualityReport,
  type TicketState,
} from '../lib/api.js';
import { hours, num, pct, sharePct, STATE_LABEL } from '../lib/format.js';
import { shortRequestType } from '../lib/labels.js';
import {
  AccessDenied,
  Badge,
  Button,
  Callout,
  ErrorState,
  Metric,
  PageHeader,
  Panel,
  Select,
  Skeleton,
  SkeletonMetrics,
  Sparkline,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  useToast,
} from '../ui/index.js';
import {
  BacklogTab,
  DemandTab,
  QualityTab,
  RecurrenceTab,
  WorkloadTab,
} from '../components/reports/panels.js';
import { SecurityTab } from '../components/reports/SecurityTab.js';
import { ChartPalette, DataTable, HBar, Meter, StackedShare } from '../components/charts.js';

interface Ctx {
  tenant: string;
  role: string | null;
  tenantName: string | undefined;
  /** /me, for this desk's capabilities (a desk may change who sees reports). */
  me?: { tenants: { id: string; capabilities?: string[] }[] };
}

/**
 * The lifecycle order the queue chart uses. Fixed, so a given state always
 * gets the same hue on every tenant and however many states are empty —
 * colour follows the entity, never its rank.
 */
const QUEUE_STATE_ORDER: TicketState[] = [
  'new',
  'open',
  'pending_requester',
  'pending_internal',
  'pending_external',
  'resolved',
];

const RUNG_NAME: Record<number, string> = {
  1: 'strip',
  2: 'machine mail',
  3: 'known template',
  4: 'pattern rules',
  5: 'systems',
  6: 'working context',
  7: 'residual',
};

/** The report tabs, in the order a reader should meet them. */
const TABS = [
  { value: 'overview', label: 'Overview', report: 'overview' },
  { value: 'workload', label: 'Workload', report: 'workload' },
  { value: 'backlog', label: 'Backlog', report: 'backlog' },
  { value: 'demand', label: 'Demand', report: 'demand' },
  { value: 'quality', label: 'Quality', report: 'quality' },
  { value: 'recurrence', label: 'Recurrence', report: 'recurrence' },
  { value: 'security', label: 'Security', report: 'security' },
] as const;

const WINDOWS = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: '365', label: 'Last 12 months' },
];

export function Dashboard() {
  const { tenant, role, tenantName, me } = useOutletContext<Ctx>();
  const allowed = canAccess(role, 'dashboard', capabilitiesIn(me, tenant));
  const toast = useToast();

  // Tab and window both live in the URL: a report somebody is arguing about
  // should be a link, not a set of directions for reproducing it.
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'overview';
  const days = params.get('days') ?? '180';
  const setParam = (key: string, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(key, value);
        return next;
      },
      { replace: true },
    );


  const ov = useQuery({
    queryKey: ['overview', tenant, days],
    queryFn: () =>
      api.get<Overview>(`/api/v1/tenants/${tenant}/reports/overview?days=${days}`),
    enabled: Boolean(tenant) && allowed,
  });
  const blocked = useQuery({
    queryKey: ['blocked', tenant],
    queryFn: () => api.get<BlockedTimeReport>(`/api/v1/tenants/${tenant}/reports/blocked-time`),
    enabled: Boolean(tenant) && allowed,
  });
  const auto = useQuery({
    queryKey: ['automation', tenant],
    queryFn: () => api.get<AutomationReport>(`/api/v1/tenants/${tenant}/reports/automation`),
    enabled: Boolean(tenant) && allowed,
  });
  // Absent (refused) when the desk has not switched suggested replies on.
  const suggested = useQuery({
    queryKey: ['suggested-replies', tenant],
    queryFn: () => api.get<SuggestedRepliesReport>(`/api/v1/tenants/${tenant}/reports/suggested-replies`),
    enabled: Boolean(tenant) && allowed,
  });
  const fields = useQuery({
    queryKey: ['field-health', tenant],
    queryFn: () =>
      api.get<{ fields: FieldHealthRow[] }>(`/api/v1/tenants/${tenant}/reports/field-health`),
    enabled: Boolean(tenant) && allowed,
  });

  // Each detail report loads only when its tab is open — they are the
  // expensive queries, and fetching all five to render one is what makes a
  // reporting screen feel slow. Written out rather than generated by a helper:
  // wrapping useQuery in a locally-defined function is a Rules of Hooks
  // violation, and that has already cost a blank page in this codebase once.
  const reportUrl = (name: string) =>
    `/api/v1/tenants/${tenant}/reports/${name}?days=${days}`;

  const workload = useQuery({
    queryKey: ['rpt-workload', tenant, days],
    queryFn: () => api.get<AgentWorkloadReport>(reportUrl('workload')),
    enabled: Boolean(tenant) && allowed && tab === 'workload',
  });
  const backlog = useQuery({
    queryKey: ['rpt-backlog', tenant, days],
    queryFn: () => api.get<BacklogAgeingReport>(reportUrl('backlog')),
    enabled: Boolean(tenant) && allowed && tab === 'backlog',
  });
  const demand = useQuery({
    queryKey: ['rpt-demand', tenant, days],
    queryFn: () => api.get<DemandShapeReport>(reportUrl('demand')),
    enabled: Boolean(tenant) && allowed && tab === 'demand',
  });
  const quality = useQuery({
    queryKey: ['rpt-quality', tenant, days],
    queryFn: () => api.get<ResponseQualityReport>(reportUrl('quality')),
    enabled: Boolean(tenant) && allowed && tab === 'quality',
  });
  const recur = useQuery({
    queryKey: ['rpt-recurrence', tenant, days],
    queryFn: () => api.get<RecurrenceReport>(reportUrl('recurrence')),
    enabled: Boolean(tenant) && allowed && tab === 'recurrence',
  });

  // Hooks above always run; only the render is gated.
  if (!allowed) {
    return <AccessDenied area="dashboard" role={role} tenantName={tenantName} />;
  }

  if (ov.error) {
    return (
      <div className="p-6">
        <ErrorState error={ov.error} />
      </div>
    );
  }

  const o = ov.data;
  // A QUIET PERIOD SAYS SO. A dash or a zero beside "SLA attainment" reads
  // as a measurement; nothing resolved means nothing was measured.
  const resolvedNone = o ? o.slaAttainment.measured === 0 && o.resolution.median === null : false;
  const arrivals30 = o ? o.dailyVolume.reduce((a, d) => a + d.created, 0) : 0;
  const tail = o?.resolution.tailRatio ?? null;

  return (
    <div className="mx-auto max-w-[1480px] p-4 sm:p-6">
      <ChartPalette />

      <PageHeader
        title="Service performance"
        description={
          o
            ? `${WINDOWS.find((w) => w.value === days)?.label ?? ''} · ${num(o.volume)} tickets · median and p90 throughout, never a mean` +
              (o.hiddenConfidential
                ? ` · ${num(o.hiddenConfidential)} confidential ticket${o.hiddenConfidential === 1 ? '' : 's'} not shown`
                : '')
            : (WINDOWS.find((w) => w.value === days)?.label ?? '')
        }
        actions={
          <div className="flex items-center gap-2">
            <Select
              value={days}
              onValueChange={(v) => setParam('days', v)}
              options={WINDOWS}
              size="sm"
              aria-label="Reporting window"
              className="w-40"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                const report = TABS.find((t) => t.value === tab)?.report ?? 'overview';
                void api
                  .download(
                    `/api/v1/tenants/${tenant}/reports/${report}/export.csv?days=${days}`,
                  )
                  .catch((err: unknown) =>
                    toast.error(
                      err instanceof Error ? err.message : 'The export could not be produced',
                    ),
                  );
              }}
            >
              <Download aria-hidden className="h-3.5 w-3.5" />
              Export CSV
            </Button>
          </div>
        }
      />

      <Tabs value={tab} onValueChange={(v) => setParam('tab', v)}>
        <TabsList className="mb-4">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="focus-visible:outline-none">
      {!o ? (
        <SkeletonMetrics />
      ) : (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Metric
            label="Median resolution"
            value={hours(o.resolution.median)}
            hint={
              resolvedNone
                ? 'Quiet: nothing was resolved in this period, so there is nothing to measure.'
                : `p90 ${hours(o.resolution.p90)} · business hours, pauses excluded`
            }
            icon={<Timer />}
          />
          <Metric
            label="Tail ratio"
            value={tail === null ? '—' : `${tail.toFixed(1)}×`}
            tone={tail !== null && tail > 5 ? 'warn' : 'default'}
            hint={
              resolvedNone
                ? 'Needs resolved tickets to measure.'
                : 'p90 divided by median. The tail, not the median, is the problem.'
            }
            icon={<Activity />}
          />
          <Metric
            label="SLA attainment"
            value={pct(o.slaAttainment.rate)}
            tone={o.slaAttainment.rate === null ? 'default' : o.slaAttainment.rate < 0.8 ? 'danger' : 'ok'}
            hint={
              o.slaAttainment.measured === 0
                ? 'Quiet: nothing was closed against a target in this period.'
                : `${num(o.slaAttainment.met)} of ${num(o.slaAttainment.measured)} closed within target`
            }
            icon={<ShieldCheck />}
          />
          <Metric
            label="Arrivals, 30 days"
            value={num(arrivals30)}
            hint={
              arrivals30 === 0
                ? 'Quiet: no new tickets in the last 30 days.'
                : 'Daily intake. Flat demand is the pattern — the record trends gently down.'
            }
            icon={<Inbox />}
            trend={<Sparkline points={o.dailyVolume.map((d) => d.created)} width={76} />}
          />
          <Metric
            label="Open backlog"
            value={num(o.backlog.open)}
            hint={`${num(o.backlog.olderThan7Days)} older than 7 days · ${num(o.backlog.unassigned)} unassigned`}
            icon={<Inbox />}
            trend={
              <Sparkline
                points={o.dailyVolume.map((d) => d.resolved)}
                width={76}
                tone="ok"
              />
            }
          />
        </div>
      )}

      {o && (
        <div className="grid gap-4 xl:grid-cols-2">
          <Panel
            title="Where the demand is"
            subtitle="Volume by request type, with the resolution spread behind it"
          >
            {o.topRequestTypes.length === 0 && <Quiet>No tickets in this period.</Quiet>}
            <HBar
              rows={o.topRequestTypes.map((t) => ({
                label: shortRequestType(t.requestType),
                value: t.tickets,
                hint: `${t.requestType}: ${num(t.tickets)} tickets · median ${hours(t.medianHours)} · p90 ${hours(t.p90Hours)}`,
              }))}
              valueLabel={(v) => num(v)}
            />
          </Panel>

          <Panel
            title="Where we miss"
            subtitle="Share of each request type breaching its resolution target"
          >
            {o.topRequestTypes.every((t) => t.breachRate === null) && (
              <Quiet>Nothing was resolved against a target in this period, so there is nothing to miss yet.</Quiet>
            )}
            <div className="space-y-1.5">
              {o.topRequestTypes
                .filter((t) => t.breachRate !== null)
                .sort((a, b) => (b.breachRate ?? 0) - (a.breachRate ?? 0))
                .slice(0, 10)
                .map((t) => (
                  <div
                    key={t.requestType}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3"
                  >
                    <Tooltip
                      content={`median ${hours(t.medianHours)} · p90 ${hours(t.p90Hours)} · ${num(t.tickets)} tickets`}
                    >
                      <span className="truncate text-xs text-ink-secondary">{t.requestType}</span>
                    </Tooltip>
                    <Meter
                      value={t.breachRate}
                      tone={
                        (t.breachRate ?? 0) > 0.4
                          ? 'danger'
                          : (t.breachRate ?? 0) > 0.2
                            ? 'warn'
                            : 'default'
                      }
                    />
                  </div>
                ))}
            </div>
          </Panel>

          <Panel
            title="State of the queue"
            subtitle="Every open ticket, by what it is waiting on"
            footer={
              <p className="text-2xs leading-relaxed text-ink-tertiary">
                Three distinct waiting states rather than one "Pending". That distinction is what
                makes the blocked-time report possible at all.
              </p>
            }
          >
            {QUEUE_STATE_ORDER.every((s) => (o.byState[s] ?? 0) === 0) && <Quiet>No open tickets.</Quiet>}
            <StackedShare
              segments={QUEUE_STATE_ORDER.filter((s) => (o.byState[s] ?? 0) > 0).map((s) => ({
                label: STATE_LABEL[s],
                value: o.byState[s] ?? 0,
              }))}
              formatValue={(v) => num(v)}
            />
          </Panel>

          <Panel
            title="How the work arrives"
            subtitle="Intake channel mix — the reason the email pipeline is the product, not a feature"
          >
            {Object.keys(o.bySource).length === 0 && <Quiet>No tickets arrived in this period.</Quiet>}
            <StackedShare
              segments={Object.entries(o.bySource)
                .sort((a, b) => b[1] - a[1])
                .map(([s, n]) => ({ label: s, value: n }))}
              formatValue={(v) => num(v)}
            />
          </Panel>

          {blocked.data && (
            <Panel
              title="Blocked time"
              subtitle="Who we are waiting on, and for how long — the anatomy of the long tail"
              className="xl:col-span-2"
              footer={
                <p className="text-2xs leading-relaxed text-ink-tertiary">
                  These tickets are not slow to do — they are waiting on somebody outside the desk.
                  Flagging that is worth more to the requester than answering faster, and it is
                  invisible in a system with a single "Pending" state.
                </p>
              }
            >
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <SubHead icon={<Clock />}>By waiting state</SubHead>
                  <HBar
                    rows={blocked.data.byState.map((b) => ({
                      label: STATE_LABEL[b.state as TicketState] ?? b.state,
                      value: b.totalHours,
                      hint: `${b.periods} periods · median ${hours(b.medianHours)} · p90 ${hours(b.p90Hours)}`,
                    }))}
                    valueLabel={(v) => hours(v)}
                  />
                </div>
                <div>
                  <SubHead icon={<AlertTriangle />}>Third parties we wait on</SubHead>
                  <HBar
                    rows={blocked.data.byParty.slice(0, 8).map((b) => ({
                      label: b.party,
                      value: b.totalHours,
                      hint: `${b.periods} waits · median ${hours(b.medianHours)}`,
                    }))}
                    valueLabel={(v) => hours(v)}
                    color="var(--c2)"
                  />
                </div>
              </div>
            </Panel>
          )}

          {auto.data && (
            <Panel
              title="Automation"
              subtitle="Whether the share of work that needs no human is actually being harvested"
            >
              <div className="grid grid-cols-2 gap-3">
                <Metric
                  label="No human reply"
                  value={pct(auto.data.zeroTouchRate)}
                  hint={`${num(auto.data.closedWithNoHumanReply)} of ${num(auto.data.totalClosed)} closed`}
                  icon={<Sparkles />}
                />
                <Metric
                  label="Never became a ticket"
                  value={num(auto.data.suppressedMail.reduce((a, s) => a + s.messages, 0))}
                  hint="Machine mail, logged and suppressed at rung 2"
                  icon={<Gauge />}
                />
              </div>

              {auto.data.templateMatches.length > 0 && (
                <div className="mt-5">
                  <SubHead icon={<Layers />}>
                    Templated forms — no classification judgement required
                  </SubHead>
                  <DataTable
                    head={['Template', 'Tickets', 'Median']}
                    rows={auto.data.templateMatches.map((t) => [
                      t.template,
                      num(t.tickets),
                      t.medianMinutes === null ? '—' : hours(t.medianMinutes / 60),
                    ])}
                  />
                </div>
              )}

              <div className="mt-5">
                <SubHead icon={<CircleDot />}>What settled each ticket</SubHead>
                <DataTable
                  head={['Ladder rung', 'Tickets']}
                  rows={auto.data.classificationByRung.map((r) => [
                    `Rung ${r.rung} — ${RUNG_NAME[r.rung] ?? 'unknown'}`,
                    num(r.tickets),
                  ])}
                />
                <p className="mt-2 text-2xs leading-relaxed text-ink-tertiary">
                  Agents overrode the proposed classification on{' '}
                  <strong className="text-ink-secondary">
                    {pct(auto.data.humanOverrides.rate, 1)}
                  </strong>{' '}
                  of tickets. That override is the training signal — the legacy category fields
                  cannot serve as ground truth.
                </p>
                {auto.data.queueMoves.some((q) => q.moved > 0) && (
                  <>
                    <p className="mt-3 text-2xs leading-relaxed text-ink-tertiary">
                      Where routing put tickets, and how often a person moved them. A queue moved out of
                      often is one whose description is sending the wrong work to it.
                    </p>
                    <DataTable
                      head={['Routed to', 'Tickets', 'Moved', 'Rate', 'Moved to']}
                      rows={auto.data.queueMoves.map((q) => [
                        q.queue,
                        num(q.tickets),
                        num(q.moved),
                        pct(q.rate, 1),
                        q.to.map((t) => `${t.queue} (${num(t.tickets)})`).join(', ') || '—',
                      ])}
                    />
                  </>
                )}
                {auto.data.humanOverrides.bySource.length > 1 && (
                  <DataTable
                    head={['Came in by', 'Classified', 'Overridden', 'Rate']}
                    rows={auto.data.humanOverrides.bySource.map((s) => [
                      s.source,
                      num(s.tickets),
                      num(s.overridden),
                      pct(s.rate, 1),
                    ])}
                  />
                )}
              </div>
            </Panel>
          )}

          {suggested.data && (
            <Panel
              title="Suggested replies"
              subtitle="Whether Suggest a reply is used, and how much of it survives into what is sent"
              footer={
                <p className="text-2xs leading-relaxed text-ink-tertiary">
                  Kept is how much of a sent suggestion went out as written, by words in order: 100% is
                  unchanged, and a light edit stays in the 90s. Not touched means it was neither used
                  nor dismissed. Last 90 days.
                </p>
              }
            >
              {suggested.data.byAgent.length === 0 ? (
                <p className="text-xs text-ink-tertiary">No replies have been suggested yet.</p>
              ) : (
                <DataTable
                  head={['Agent', 'Suggested', 'Sent', 'Unchanged', 'Kept', 'In composer', 'Dismissed', 'Not touched']}
                  rows={suggested.data.byAgent.map((a) => [
                    a.agent,
                    num(a.suggested),
                    num(a.sent),
                    num(a.sentUnchanged),
                    pct(a.meanSimilarity, 0),
                    num(a.usedNotSent),
                    num(a.dismissed),
                    num(a.ignored),
                  ])}
                />
              )}
            </Panel>
          )}

          {fields.data && (
            <Panel
              title="Field health"
              subtitle="Every field a ticket can carry, and whether it is telling you anything"
              footer={
                <p className="text-2xs leading-relaxed text-ink-tertiary">
                  A field earns its place by discriminating between tickets. One that is empty,
                  or that holds the same value on everything, costs a moment of everyone&rsquo;s
                  attention on every ticket and returns nothing — and it is invisible until
                  somebody counts. This is that count.
                </p>
              }
            >
              <FieldHealthList
                rows={fields.data.fields.filter((f) => f.origin === 'live')}
                heading="Fields configured here"
                blurb="Defined on the Configuration screen. Each one names the report, rule or workflow that consumes it."
                empty="No custom fields are configured for this group."
              />
              <FieldHealthList
                rows={fields.data.fields.filter((f) => f.origin === 'legacy')}
                heading="Fields inherited from FreshService"
                blurb="Imported for provenance and never used to route, prioritise or report. Listed here because their state is the evidence for that decision."
                className="mt-5"
              />
            </Panel>
          )}
        </div>
      )}
        </TabsContent>

        <TabsContent value="workload" className="focus-visible:outline-none">
          <ReportPane query={workload}>{(d) => <WorkloadTab data={d} />}</ReportPane>
        </TabsContent>

        <TabsContent value="backlog" className="focus-visible:outline-none">
          <ReportPane query={backlog}>{(d) => <BacklogTab data={d} tenant={tenant} />}</ReportPane>
        </TabsContent>

        <TabsContent value="demand" className="focus-visible:outline-none">
          <ReportPane query={demand}>{(d) => <DemandTab data={d} />}</ReportPane>
        </TabsContent>

        <TabsContent value="quality" className="focus-visible:outline-none">
          <ReportPane query={quality}>{(d) => <QualityTab data={d} />}</ReportPane>
        </TabsContent>

        <TabsContent value="security" className="focus-visible:outline-none">
          <SecurityTab tenant={tenant} />
        </TabsContent>

        <TabsContent value="recurrence" className="focus-visible:outline-none">
          <ReportPane query={recur}>{(d) => <RecurrenceTab data={d} />}</ReportPane>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Loading, error and empty handling for one report pane, in one place.
 *
 * Without this each of the five tabs grows its own `isLoading &&` /
 * `error &&` pair, they drift, and one of them eventually renders a white
 * screen on a slow query.
 */
function ReportPane<T>({
  query,
  children,
}: {
  query: { data: T | undefined; isLoading: boolean; error: unknown };
  children: (data: T) => React.ReactNode;
}) {
  if (query.error) return <ErrorState error={query.error} />;
  if (query.isLoading || !query.data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[5.5rem] w-full" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
        <div className="flex justify-center py-2">
          <Spinner />
        </div>
      </div>
    );
  }
  return <>{children(query.data)}</>;
}

function SubHead({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <h3 className="mb-2.5 flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
      <span aria-hidden className="[&>svg]:h-3 [&>svg]:w-3">
        {icon}
      </span>
      {children}
    </h3>
  );
}

/**
 * One population of fields — the ones configured here, or the ones inherited
 * from FreshService.
 *
 * The counts are spelled out rather than left as a percentage. "0% of tickets
 * carry a value" reads as a defect in the field; "0 of 873" reads as what it
 * is, which is that nothing has ever written to it.
 */
function FieldHealthList({
  rows,
  heading,
  blurb,
  empty,
  className,
}: {
  rows: FieldHealthRow[];
  heading: string;
  blurb: string;
  empty?: string;
  className?: string;
}) {
  if (rows.length === 0 && !empty) return null;
  const suspect = rows.filter((f) => f.suspect).length;

  return (
    <div className={className}>
      <SubHead icon={<Layers />}>
        {heading}
        {rows.length > 0 && (
          <span className="font-normal normal-case tracking-normal text-ink-tertiary">
            · {suspect} of {rows.length} carrying nothing
          </span>
        )}
      </SubHead>
      <p className="mb-2.5 text-2xs leading-relaxed text-ink-tertiary">{blurb}</p>

      {rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-ink-tertiary">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((f) => (
            <li
              key={`${f.origin}:${f.key}`}
              className="rounded-md border border-line-subtle bg-surface-sunken/40 p-2.5"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-xs font-medium text-ink">{f.label}</span>
                <Meter value={f.fillRate} tone={f.suspect ? 'warn' : 'default'} />
              </div>
              <p className="mt-1 flex flex-wrap items-center gap-1.5 text-2xs text-ink-tertiary">
                <Badge tone="neutral">
                  {num(f.distinctValues)} value{f.distinctValues === 1 ? '' : 's'}
                </Badge>
                <span>
                  {num(f.filled)} of {num(f.tickets)} filled
                </span>
                {f.dominantValue !== null && f.dominantShare !== null && (
                  <span className="truncate">
                    most common <span className="font-mono text-ink-secondary">{f.dominantValue}</span>{' '}
                    ({sharePct(f.dominantShare)})
                  </span>
                )}
              </p>
              <p className="mt-1 truncate text-2xs text-ink-tertiary">{f.consumer}</p>
              {f.reason && (
                <Callout tone="warn" className="mt-2 py-1.5">
                  {f.reason}
                </Callout>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Said in words where a panel has nothing to show, rather than an empty chart. */
function Quiet({ children }: { children: ReactNode }) {
  return <p className="text-xs text-ink-tertiary">{children}</p>;
}
